/**
 * The Engagements tab inside Client detail (f7.1).
 *
 * **This is not a screen.** There is no route, no `SCREENS` entry, no
 * `DETAIL_ROUTES` entry and no navigation item -- `navigation.ts` already said
 * so in a comment before this feature existed: *"Engagements deliberately have
 * NO entry: an engagement is administered inside Client detail, never from the
 * navigation (ADR-17)."* This component is ONE entry in the declared tab array
 * f6.1 built, and `navigation.ts`, `App.tsx` and `permissions.ts` are not
 * edited.
 *
 * **No new CSS is written.** `.toolbar`, `.search-field`, `.card`,
 * `.table-scroll`, `table.data`, `.row-actions`, `.pill-*` and the `.tabs`
 * family are all already in `styles/index.css`. Every colour comes from a CSS
 * custom property defined in the prototype's token file; **no brand hex
 * literal appears anywhere in this component.**
 *
 * **What the prototype draws that is deliberately changed**, so a reviewer can
 * check the list rather than file defects:
 *
 * | Prototype element | What is built | Why |
 * | --- | --- | --- |
 * | A `Planned end` column | Replaced by `End` | `End Date` is populated on **70 of 70** legacy rows and `Planned end` on **1**. The prototype shows the column the legacy does not fill |
 * | Page controls | **Absent** | Engagements per client run 1 to 7, and 26 of the 39 clients have exactly one. f6.1 paginates because 39 rows across four pages is a real experience; seven rows is not |
 * | Sample rows for The Blackstone Group | **Absent** | adr-16 forbids seeding hierarchy; the table ships empty and the 70 arrive with f8.2 |
 * | A `Currency` field in the dialog | **Absent** | Every Business Unit is single-currency, so it is determined two levels up |
 *
 * Every value on screen is bound to the server's answer. Nothing here derives
 * a status label, a stage label, a permission or a visibility rule in the
 * browser.
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  ChevronDown,
  FileSignature,
  Pencil,
  Plus,
  RotateCcw,
  RotateCw,
  Search,
} from 'lucide-react';

import type { Client, Engagement } from '@/api/client';
import { useClientEngagements, useReactivateEngagement } from '@/api/queries';
import { CREATE_CHILD, EDIT_NODE, rowCan } from '@/session/permissions';
import { EmptyCell } from '@/ui/EmptyCell';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, type SortState } from '@/ui/tableSort';

import { DeactivateEngagementDialog } from './DeactivateEngagementDialog';
import { ElementExpansion } from './ElementExpansion';
import { EngagementDialog } from './EngagementDialog';
import {
  DEFAULT_SORT,
  NO_FILTERS,
  badgeClassFor,
  formatDate,
  initialsOf,
  isActive,
  isFiltered,
  isRefusal,
  projectHeadFor,
  stageClassFor,
  stageOptions,
  statusOptions,
  visibleEngagements,
  type EngagementFilters,
  type SortColumn,
} from './engagementView';

/**
 * The table's column count, for the `colSpan` an empty state and an expanded
 * region both need. Declared once so adding a column cannot leave either
 * spanning the wrong width.
 */
const COLUMN_COUNT = 11;

export function EngagementsPanel({
  client,
  mayManage,
}: {
  client: Client;
  /**
   * SESSION-level `hierarchy:manage`: whether the list-level `New engagement`
   * control is rendered at all. Whether it is ENABLED is the client row's own
   * `hierarchy:create_child`, and each engagement's controls are its own
   * `permissions` (f9.2). Never from a role code.
   */
  mayManage: boolean;
}) {
  const engagements = useClientEngagements(client.id);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<EngagementFilters>(NO_FILTERS);
  const [sort, setSort] = useState<SortState<SortColumn>>(DEFAULT_SORT);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Engagement | null>(null);
  const [deactivating, setDeactivating] = useState<Engagement | null>(null);
  /** Which rows are expanded. A set, because several can be open at once. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const all = engagements.data?.engagements ?? [];
  const visible = useMemo(
    () => visibleEngagements(all, { search, filters, sort }),
    [all, search, filters, sort],
  );

  if (engagements.isPending) {
    return (
      <div className="card" aria-busy="true" aria-label="Loading the Engagements">
        <div className="card-body">
          {[0, 1, 2].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    );
  }

  if (engagements.error) {
    /*
     * A 403 is a refusal, not a failure -- and it never triggers the
     * interactive re-authentication a 401 does (f1.2).
     */
    if (isRefusal(engagements.error)) {
      return (
        <StateCard warning title="No access">
          <p>
            The server refuses this client&rsquo;s engagements for your account,
            and records the refusal in the audit log.
          </p>
        </StateCard>
      );
    }
    return (
      <StateCard warning title="The Engagements could not be loaded">
        <p>
          {engagements.error instanceof Error
            ? engagements.error.message
            : 'Unexpected error.'}
        </p>
        <p className="empty-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void engagements.refetch()}
          >
            <RotateCw aria-hidden="true" /> Retry
          </button>
        </p>
      </StateCard>
    );
  }

  const toggleSort = (column: SortColumn) =>
    setSort((state) => nextSort(state, column));
  const setFilter = (field: keyof EngagementFilters, value: string) =>
    setFilters((state) => ({ ...state, [field]: value }));

  const toggleRow = (engagementId: string) =>
    setExpanded((open) => {
      const next = new Set(open);
      if (next.has(engagementId)) next.delete(engagementId);
      else next.add(engagementId);
      return next;
    });

  /*
   * An inactive client cannot be the parent of a NEW engagement -- the seam
   * f6.1 built and named for this feature. The control is disabled and SAYS
   * WHY rather than being hidden, and the server refuses one anyway: the
   * client is never the security boundary.
   */
  const clientIsActive = client.status.code === 'ACTIVE';
  const clientAdmitsChild = rowCan(client, CREATE_CHILD);
  const canCreate = mayManage && clientIsActive && clientAdmitsChild;
  const createTitle = canCreate
    ? undefined
    : !clientAdmitsChild
      ? 'You cannot create engagements under this client.'
      : 'This client is inactive, so it cannot be the parent of a new engagement. Reactivate it first.';

  return (
    <>
      <div className="toolbar">
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search engagement or KE code"
            aria-label="Search engagement or KE code"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {/*
          Options are the values actually present, not every value the server
          knows: a filter offering a stage no row carries is a worse control
          than one offering the ones that do (f4.2's rule).
        */}
        <select
          className="toolbar-select"
          aria-label="Filter by delivery stage"
          value={filters.stageCode}
          onChange={(event) => setFilter('stageCode', event.target.value)}
        >
          <option value="">All stages</option>
          {stageOptions(all).map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by status"
          value={filters.statusCode}
          onChange={(event) => setFilter('statusCode', event.target.value)}
        >
          <option value="">All statuses</option>
          {statusOptions(all).map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
        <div className="spacer" />
        {mayManage && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!canCreate}
            title={createTitle}
            onClick={() => setCreating(true)}
          >
            <Plus aria-hidden="true" /> New engagement
          </button>
        )}
      </div>

      <div className="card">
        <div className="table-scroll">
          <table className="data zebra">
            <thead>
              <tr>
                <th scope="col" className="audit-toggle-cell">
                  <span className="sr-only">Delivery elements</span>
                </th>
                <SortableHeader
                  column="code"
                  label="KE code"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="name"
                  label="Engagement"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="projectHead"
                  label="Project Head"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="productType"
                  label="Product type"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="stage"
                  label="Delivery stage"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="start"
                  label="Start"
                  sort={sort}
                  onSort={toggleSort}
                />
                {/*
                  `End` replaces the prototype's `Planned end`: End Date is
                  populated on 70 of 70 legacy rows and Planned end on 1.
                */}
                <SortableHeader
                  column="end"
                  label="End"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="elements"
                  label="Elements"
                  className="num"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="status"
                  label="Status"
                  sort={sort}
                  onSort={toggleSort}
                />
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT}>
                    <EmptyState
                      searching={search.trim().length > 0 || isFiltered(filters)}
                      mayCreate={clientAdmitsChild}
                      clientName={client.name}
                    />
                  </td>
                </tr>
              ) : (
                visible.map((engagement) => (
                  <EngagementRow
                    key={engagement.id}
                    engagement={engagement}
                    clientId={client.id}
                    expanded={expanded.has(engagement.id)}
                    onToggle={() => toggleRow(engagement.id)}
                    onEdit={() => setEditing(engagement)}
                    onDeactivate={() => setDeactivating(engagement)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <EngagementDialog
          client={client}
          engagement={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {deactivating && (
        <DeactivateEngagementDialog
          engagement={deactivating}
          onClose={() => setDeactivating(null)}
        />
      )}
    </>
  );
}

function StateCard({
  warning = false,
  title,
  children,
}: {
  warning?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>
            <AlertTriangle aria-hidden="true" />
          </div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * TWO empty states, because on day one of phase 7 two things are true at once.
 *
 * A reader who has narrowed the list to nothing needs to be told to widen it;
 * a reader looking at a client that genuinely has no engagements needs to be
 * told where they come from. One message would be misleading to whichever
 * reader it did not describe.
 */
/*
 * f9.2: no "Nothing in your scope" variant here, deliberately. A caller who
 * can open this client sees every engagement under it (the walk-down reaches
 * them through the client), so an empty tab always means the client has none.
 */
function EmptyState({
  searching,
  mayCreate,
  clientName,
}: {
  searching: boolean;
  mayCreate: boolean;
  clientName: string;
}) {
  if (searching) {
    return (
      <div className="empty">
        <div className="e-icon">
          <Search aria-hidden="true" />
        </div>
        <h3>No engagements match</h3>
        <p>Clear the search and the two filters to see the rest.</p>
      </div>
    );
  }

  return (
    <div className="empty">
      <div className="e-icon">
        <FileSignature aria-hidden="true" />
      </div>
      <h3>No engagements under {clientName}</h3>
      <p>
        {mayCreate
          ? 'Create the first one, or wait for the master workbook import to bring this client’s engagements across.'
          : 'Nothing has been recorded against this client yet.'}
      </p>
    </div>
  );
}

function EngagementRow({
  engagement,
  clientId,
  expanded,
  onToggle,
  onEdit,
  onDeactivate,
}: {
  engagement: Engagement;
  clientId: string;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDeactivate: () => void;
}) {
  const reactivate = useReactivateEngagement();
  const busy = reactivate.isPending;
  const active = isActive(engagement);
  const head = projectHeadFor(engagement);
  const panelId = `elements-${engagement.id}`;
  /*
   * The ROW's own capability. The element routes declare the same capability
   * as an engagement edit, so it governs the element controls too.
   */
  const mayEdit = rowCan(engagement, EDIT_NODE);

  return (
    <>
      <tr className={expanded ? 'is-expanded' : undefined}>
        <td className="audit-toggle-cell">
          <button
            type="button"
            className="icon-btn audit-toggle"
            aria-expanded={expanded}
            aria-controls={panelId}
            aria-label={
              expanded
                ? `Hide the delivery elements of ${engagement.name}`
                : `Show the delivery elements of ${engagement.name}`
            }
            onClick={onToggle}
          >
            {/*
              The same chevron f1.5's audit table uses, rotated by the shared
              `.audit-toggle[aria-expanded='true'] svg` rule -- so the
              expansion affordance is identical on both screens rather than
              being a second thing a reader has to learn.
            */}
            <ChevronDown aria-hidden="true" />
          </button>
        </td>
        <td className="mono">{engagement.code}</td>
        <td>{engagement.name}</td>
        <td>
          {head ? (
            <span className="row owner-cell">
              <span className="avatar sm" aria-hidden="true">
                {initialsOf(head.display_name)}
              </span>
              {head.display_name}
            </span>
          ) : (
            /*
             * Reports the ABSENCE OF AN ASSIGNMENT and nothing more -- never a
             * client-side inference about permissions.
             */
            <span className="badge pill-warning">Not assigned</span>
          )}
        </td>
        <td>{engagement.product_type ?? <EmptyCell />}</td>
        <td>
          {engagement.delivery_stage ? (
            <span className={stageClassFor(engagement.delivery_stage.code)}>
              {engagement.delivery_stage.name}
            </span>
          ) : (
            <EmptyCell />
          )}
        </td>
        <td>{formatDate(engagement.start_date) ?? <EmptyCell />}</td>
        <td>{formatDate(engagement.end_date) ?? <EmptyCell />}</td>
        <td className="num">{engagement.element_count}</td>
        <td>
          <span className={badgeClassFor(engagement.status.code)}>
            {engagement.status.name}
          </span>
        </td>
        <td>
          <div className="row-actions">
            {mayEdit && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="Edit"
                  aria-label={`Edit ${engagement.name}`}
                  disabled={busy}
                  onClick={onEdit}
                >
                  <Pencil aria-hidden="true" />
                </button>
                {active ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    title="Deactivate"
                    aria-label={`Deactivate ${engagement.name}`}
                    disabled={busy}
                    onClick={onDeactivate}
                  >
                    <Ban aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    title="Reactivate"
                    aria-label={`Reactivate ${engagement.name}`}
                    disabled={busy}
                    /*
                     * Unguarded and unconfirmed: reactivating a parent cannot
                     * orphan anything, and it grants nothing new.
                     */
                    onClick={() => reactivate.mutate(engagement.id)}
                  >
                    <RotateCcw aria-hidden="true" />
                  </button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr id={panelId}>
          <td />
          <td colSpan={COLUMN_COUNT - 1}>
            <ElementExpansion
              engagement={engagement}
              clientId={clientId}
              mayManage={mayEdit}
            />
          </td>
        </tr>
      )}
    </>
  );
}
